<script  lang="ts">
  import AddPlayer from "@frontend/Components/AddPlayer.svelte";
  import Modal from "@frontend/Components/Modal.svelte";
  import WithdrawDepositForm from "@frontend/Components/WithdrawDepositForm.svelte";
  import WithdrawCashierForm from "@frontend/Components/WithdrawCashierForm.svelte";
  import { service } from "@frontend/services";
  import { GetCachedToken, me, RefreshMe } from "@frontend/user";
  import { type ApiResultOfPublicUserDTO, type User, type WalletTransaction, UserRole, type PublicUserDTO } from "@frontend/walletapi";
  import { onDestroy, onMount } from "svelte";
  import { push, location } from "svelte-spa-router";
  import EditPlayerForm from "../Components/EditPlayerForm.svelte";
  import { formatDate, formatDateTime, formatMoney } from "./Formats";
  import { get } from "svelte/store";
  import { fade } from 'svelte/transition';
  import EditUserForm from "@frontend/Components/EditUserForm.svelte";
  import PromotionalcreditsForm from "@frontend/Components/PromotionalcreditsForm.svelte";
  import { translate, currentLanguage } from "../Translator";
  import { groups } from "@frontend/grouplist";
  import { RefreshGroups } from "@frontend/grouplist";


  let currentPage = 1;
  let playersPerPage = 20;
  let paginatedPlayers: User[] = [];
  let totalPages = 1;
  let totalItems = 0;
  let loadingPlayers = true;
  let lastKnownPage = 1;
  let isNavigatingProgrammatically = false;
  let playersLoaded = false;
  let playerListError = false;
  let playerRequestSequence = 0;
  let loadedPage = 1;
  let loadedSearch = '';
  let searchTimer = 0;
  let lastSearchValue = '';
  let paginationPage = 1;
  $: paginationPage = playersLoaded && (loadingPlayers || playerListError || searchTimer > 0)
    ? loadedPage
    : currentPage;

  // Only players of this location (group), from ?group= so the Locations page can link here.
  let groupFilter: number | undefined = readGroupFromUrl();

  function readGroupFromUrl(): number | undefined {
    const hashParts = window.location.hash.split('?');
    if (hashParts.length < 2) return undefined;
    const groupId = Number(new URLSearchParams(hashParts[1]).get('group'));
    return Number.isSafeInteger(groupId) && groupId > 0 ? groupId : undefined;
  }

  // The list's query string: its page (always when keepPage, as the player links did) and location.
  function listQuery(page: number, keepPage: boolean = false): string {
    const query = new URLSearchParams();
    if (page > 1 || keepPage) query.set('page', String(page));
    if (groupFilter) query.set('group', String(groupFilter));
    const text = query.toString();
    return text ? `?${text}` : '';
  }

  function groupChanged() {
    currentPage = 1;
    lastKnownPage = 1;
    updateUrlWithPage(1);
    void loadPlayers();
  }

  // Locations the logged-in user may see (as the wallet allows: own location and below, an admin
  // also its parent), by their full path.
  function canSeeGroup(groupId: number): boolean {
    if ($me?.Role === UserRole.Owner) return true;
    const myGroupId = $me?.GroupId;
    if (!myGroupId) return false;
    if ($me?.Role === UserRole.Admin && $groups.find(g => g.Id === myGroupId)?.ParentId === groupId) return true;
    let current = $groups.find(g => g.Id === groupId);
    for (let depth = 0; current && depth < 20; depth++) {
      if (current.Id === myGroupId) return true;
      current = current.ParentId ? $groups.find(g => g.Id === current?.ParentId) : undefined;
    }
    return false;
  }

  $: groupOptions = $groups
    .filter(g => canSeeGroup(g.Id))
    .map(g => ({ Id: g.Id, Path: groupPathName(g) }))
    .sort((a, b) => a.Path.localeCompare(b.Path));

  function groupPathName(group: { Id: number, Name?: string, ParentId?: number }): string {
    const names: string[] = [];
    let current: { Id: number, Name?: string, ParentId?: number } | undefined = group;
    while (current && names.length < 20) {
      names.unshift(current.Name ?? String(current.Id));
      current = current.ParentId ? $groups.find(g => g.Id === current?.ParentId) : undefined;
    }
    return names.join(' / ');
  }

  function updateUrlWithPage(page: number) {
    isNavigatingProgrammatically = true;
    const pageQuery = listQuery(page);
    push(`#/PlayerList${pageQuery}`);
    setTimeout(() => {
      isNavigatingProgrammatically = false;
    }, 100);
  }
  
  
  type PromoAction =
  {
    action: "promo",
    userid: number
  };

  type WithdrawAction =
  {
    action: "withdraw",
    userid: number
  };

  type WithdrawcashierAction =
  {
    action: "withdrawcashier",
    userid: number
  };


  type DepositAction =
  {
    action: "deposit",
    userid: number;
  };

  type EditAction =
  {
    action: "edit",
    userid: number;
  };

  type AddAction =
  {
    action: "add"
  };

  type RouteParams =
    WithdrawAction |
    WithdrawcashierAction |
    DepositAction |
    EditAction |
    AddAction |
    PromoAction |
    undefined;



  export let params: RouteParams = undefined;

  let selectedUser: User | undefined = undefined;
  let loadingSelectedUser = false;
  let selectedUserError = '';
  let selectedUserRequest = 0;

  $: resolveSelectedUser(params, paginatedPlayers);

  async function resolveSelectedUser(route: RouteParams, pagePlayers: User[]) {
    const request = ++selectedUserRequest;
    selectedUserError = '';
    selectedUser = undefined;
    loadingSelectedUser = false;
    if (!route || !('userid' in route)) return;

    const playerId = Number(route.userid);
    if (!Number.isSafeInteger(playerId) || playerId <= 0) return;

    const pagePlayer = pagePlayers.find(player => player.Id === playerId);
    if (pagePlayer) {
      selectedUser = pagePlayer;
      return;
    }

    loadingSelectedUser = true;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      const response = await Promise.race([
        service.walletapi.playerListPaged({
          Token: GetCachedToken(),
          Data: { Page: 1, PageSize: 1, SearchName: undefined, PlayerId: playerId, GameId: undefined, GroupId: undefined }
        }),
        new Promise<never>((_, reject) => {
          timeout = setTimeout(() => reject(new Error('Player lookup timed out')), 15000);
        })
      ]);
      if (request !== selectedUserRequest) return;
      if (!response?.Success || !response.Data) {
        selectedUserError = response?.ErrorMessage || 'Unable to load player. Please close and try again.';
        return;
      }
      selectedUser = response.Data.Items?.find(player => player.Id === playerId);
    } catch (error) {
      if (request === selectedUserRequest) {
        selectedUserError = 'Unable to load player. Please close and try again.';
      }
    } finally {
      if (timeout !== undefined) clearTimeout(timeout);
      if (request === selectedUserRequest) loadingSelectedUser = false;
    }
  }

  onDestroy(() => {
    ++selectedUserRequest;
    ++playerRequestSequence;
    if (searchTimer) window.clearTimeout(searchTimer);
  });

  let searchInput: string = "";

  let message = { text: '', type: '' };

  function readPageFromUrl(): number | null {
    const hash = window.location.hash;
    const hashParts = hash.split('?');
    let pageParam = null;
    
    if (hashParts.length > 1) {
      const hashQueryParams = new URLSearchParams(hashParts[1]);
      pageParam = hashQueryParams.get('page');
    }
    
    if (pageParam) {
      const pageNumber = parseInt(pageParam, 10);
      if (pageNumber > 0) {
        return pageNumber;
      }
    }
    return null;
  }

  async function loadPlayers() {
    const request = ++playerRequestSequence;
    const requestPage = currentPage;
    const requestSearch = searchInput.trim();
    let timeout: ReturnType<typeof setTimeout> | undefined;
    loadingPlayers = true;
    playerListError = false;
    try {
      const response = await Promise.race([
        service.walletapi.playerListPaged({
          Token: GetCachedToken(),
          Data: {
            Page: requestPage,
            PageSize: playersPerPage,
            SearchName: requestSearch || undefined,
            PlayerId: undefined,
            GameId: undefined,
            GroupId: groupFilter
          }
        }),
        new Promise<never>((_, reject) => {
          timeout = setTimeout(() => reject(new Error('Player list request timed out')), 15000);
        })
      ]);

      if (request !== playerRequestSequence) return;

      if (response?.Success && response.Data) {
        const responseTotalPages = response.Data.TotalPages || 1;
        if (requestPage > responseTotalPages) {
          currentPage = responseTotalPages;
          lastKnownPage = responseTotalPages;
          updateUrlWithPage(responseTotalPages);
          void loadPlayers();
          return;
        }

        paginatedPlayers = response.Data.Items || [];
        totalPages = responseTotalPages;
        totalItems = response.Data.TotalItems || 0;
        loadedPage = requestPage;
        loadedSearch = requestSearch;
        playersLoaded = true;
      } else {
        playerListError = true;
      }
    } catch (error) {
      if (request === playerRequestSequence) playerListError = true;
    } finally {
      if (timeout !== undefined) clearTimeout(timeout);
      if (request === playerRequestSequence) loadingPlayers = false;
    }
  }

  onMount(() => {
    if (!$me) {
      push("/user/Login");
    }

    const pageFromUrl = readPageFromUrl();
    if (pageFromUrl) {
      currentPage = pageFromUrl;
      lastKnownPage = pageFromUrl;
    }

    const unregisterUserChanged = service.walletapi.registerOperation(
      "UserChanged", "PlayerList/UserChanged", user => {
        if (user.Role !== UserRole.Player) return;
        const mergeUser = (oldUser: User) => ({
          ...user,
          LastWalletTransaction: user.LastWalletTransaction || oldUser.LastWalletTransaction,
          LastWalletTransactionId: user.LastWalletTransactionId || oldUser.LastWalletTransactionId
        });
        if (paginatedPlayers.some(player => player.Id === user.Id)) {
          paginatedPlayers = paginatedPlayers.map(player => player.Id === user.Id ? mergeUser(player) : player);
        }
        if (selectedUser?.Id === user.Id) selectedUser = mergeUser(selectedUser);
      }
    );
    const unregisterUserAdded = service.walletapi.registerOperation(
      "UserAdded", "PlayerList/UserAdded", user => {
        if (user.Role === UserRole.Player) void loadPlayers();
      }
    );
    const unregisterNewTransaction = service.walletapi.registerOperation(
      "NewTransaction", "PlayerList/NewTransaction", transaction => {
        const updatePlayer = (player: User) => ({
          ...player,
          LastWalletTransactionId: transaction.Id,
          LastWalletTransaction: transaction
        });
        if (paginatedPlayers.some(player => player.Id === transaction.UserId)) {
          paginatedPlayers = paginatedPlayers.map(player =>
            player.Id === transaction.UserId ? updatePlayer(player) : player
          );
        }
        if (selectedUser?.Id === transaction.UserId) selectedUser = updatePlayer(selectedUser);
      }
    );
    return () => {
      unregisterUserChanged();
      unregisterUserAdded();
      unregisterNewTransaction();
    };
  });


  // A link or back/forward to another location's list.
  $: if ($location && !isNavigatingProgrammatically) {
    const groupFromUrl = readGroupFromUrl();
    if (groupFromUrl !== groupFilter) {
      groupFilter = groupFromUrl;
      if (playersLoaded) {
        currentPage = 1;
        lastKnownPage = 1;
        void loadPlayers();
      }
    }
  }

  $: {
    if ($location && !isNavigatingProgrammatically) {
      const pageFromUrl = readPageFromUrl();

      if (pageFromUrl) {
        if (pageFromUrl !== currentPage) {
          currentPage = pageFromUrl;
          lastKnownPage = pageFromUrl;
          if (playersLoaded) {
            loadPlayers();
          }
        }
      } else {
        // Solo resetear a página 1 si realmente estamos en /PlayerList sin parámetros
        // y no hay una acción activa, pero solo si no estamos en medio de una carga inicial
        if ($location === '/PlayerList' && !params?.action && playersLoaded) {
          if (currentPage !== 1) {
            currentPage = 1;
            lastKnownPage = 1;
            loadPlayers();
          }
        } else if (currentPage !== lastKnownPage && lastKnownPage > 0) {
          currentPage = lastKnownPage;
          if (playersLoaded) {
            loadPlayers();
          }
        }
      }
    }
  }

  onMount(async () => {
    // Después del onMount síncrono, verificar si hay página en la URL y preservarla
    const pageFromUrl = readPageFromUrl();
    if (pageFromUrl && pageFromUrl !== currentPage) {
      currentPage = pageFromUrl;
      lastKnownPage = pageFromUrl;
    }

    await Promise.all([loadPlayers(), RefreshGroups()]);
  });


  function getTotalPages(): number {
    return totalPages;
  }

  function goToPage(page: number) {
    if (loadingPlayers || searchTimer > 0) return;
    if (page >= 1 && page <= getTotalPages()) {
      currentPage = page;
      lastKnownPage = page;
      updateUrlWithPage(page);
      loadPlayers();
    }
  }

  function previousPage() {
    if (paginationPage > 1) {
      goToPage(paginationPage - 1);
    }
  }

  function nextPage() {
    if (paginationPage < getTotalPages()) {
      goToPage(paginationPage + 1);
    }
  }

  function getPageRange() {
    const totalPages = getTotalPages();
    const current = paginationPage;
    const delta = 2; 
    
    let start = Math.max(1, current - delta);
    let end = Math.min(totalPages, current + delta);
    
    if (end - start < 4) {
      if (start === 1) {
        end = Math.min(totalPages, start + 4);
      } else {
        start = Math.max(1, end - 4);
      }
    }
    
    return Array.from({length: end - start + 1}, (_, i) => start + i);
  }

  function dismiss()
  {
    const page = playersLoaded ? loadedPage : currentPage;
    const pageQuery = listQuery(page);
    push(`#/PlayerList${pageQuery}`);
  }

  function retryPlayerLoad() {
    if (searchTimer) {
      window.clearTimeout(searchTimer);
      searchTimer = 0;
      currentPage = 1;
      lastKnownPage = 1;
      updateUrlWithPage(1);
    }
    void loadPlayers();
  }




  $: if (searchInput !== lastSearchValue) {
    lastSearchValue = searchInput;
    searchChanged();
  }

  function clearSearch() {
    searchInput = '';
  }


  function searchChanged()
  {
    if (searchTimer != 0) window.clearTimeout(searchTimer);
    ++playerRequestSequence;
    playerListError = false;
    searchTimer = window.setTimeout(applySearch, 500);
  }

  function applySearch()
  {
    searchTimer = 0;
    currentPage = 1;
    lastKnownPage = 1;
    updateUrlWithPage(1);
    void loadPlayers();
  }

  async function DisablePlayer(user: User) {
    try {
      user.Enabled = !user.Enabled;
      
      const result = await service.walletapi.disableUser({
        Token: GetCachedToken(),
        Data: {
          Enabled: user.Enabled,
          Id: user.Id
        }
      });

      if (result.Success) {
        paginatedPlayers = paginatedPlayers.map(p =>
          p.Id === user.Id ? { ...p, Enabled: user.Enabled } : p
        );

        message = {
          text: `Player ${user.FirstName} has been ${user.Enabled ? 'enabled' : 'disabled'} successfully`,
          type: user.Enabled ? 'success' : 'negative'
        };
      } else {
        user.Enabled = !user.Enabled;
        message = {
          text: result.Message || 'Error updating player status',
          type: 'error'
        };
      }
    } catch (err) {
      // Si hay error, revertir el cambio
      user.Enabled = !user.Enabled;
      message = {
        text: err instanceof Error ? err.message : 'An unexpected error occurred',
        type: 'error'
      };
    }

    setTimeout(() => {
      message = { text: '', type: '' };
    }, 3000);
  }

  function CanWithdraw(lastTransaction: WalletTransaction | undefined): boolean
  {
    if (!lastTransaction)
    {
      return false;
    }

    const moneyTransactionBalance = lastTransaction.MoneyTransaction?.[0]?.Balance?.InCents ?? 0;
    if (moneyTransactionBalance > 0)
    {
      return true;
    }

    const cashableBalance = lastTransaction.CashableBalance?.InCents ?? 0;
    if (cashableBalance > 0)
    {
      return true;
    }

    return false;
  }

  function CanDeposit(user: User): boolean
  {
    if (!user.Enabled)
    {
      return false;
    }

    return true;
  }

  function CanWithdrawForUser(user: User, lastTransaction: WalletTransaction | undefined): boolean
  {
    if (!user.Enabled)
    {
      return false;
    }

    return CanWithdraw(lastTransaction);
  }

  function sumPromotionBalances(promoArr: any[]) {
    if (!promoArr || promoArr.length === 0) return 0;
    return promoArr.reduce((sum: number, promo: any) => sum + (promo.Balance?.InCents || 0), 0);
  }


  function getGroupPath(group: any): string {
    if (!group || !$me?.GroupId) return group?.Name || '';
    
    const myGroupId = Number($me.GroupId);
    const playerGroupId = Number(group.Id);
    
    // Si el jugador está en mi mismo grupo
    if (playerGroupId === myGroupId) {
      return '📁';
    }
    
    // Construir la jerarquía completa del grupo del jugador (de arriba hacia abajo)
    let pathParts: string[] = [];
    let currentGroup: any = group;
    
    // Primero construir el array de grupos desde el jugador hasta la raíz
    while (currentGroup) {
      if (currentGroup.Name) {
        pathParts.unshift(currentGroup.Name);
      }
      if (currentGroup.ParentId) {
        currentGroup = $groups.find(g => g.Id === currentGroup.ParentId);
      } else {
        break;
      }
    }
    
    // Buscar si mi grupo está en la jerarquía del jugador
    let myGroupIndex = -1;
    currentGroup = group;
    
    // Recorrer desde el grupo del jugador hacia arriba buscando mi grupo
    let index = pathParts.length - 1;
    while (currentGroup) {
      const currentGroupId = Number(currentGroup.Id);
      if (currentGroupId === myGroupId) {
        myGroupIndex = index;
        break;
      }
      if (currentGroup.ParentId) {
        currentGroup = $groups.find(g => g.Id === currentGroup.ParentId);
        index--;
      } else {
        break;
      }
    }
    
    // Si mi grupo está en la jerarquía, reemplazar con "📁" y mantener los subgrupos
    if (myGroupIndex >= 0) {
      // Tomar solo desde mi grupo hacia abajo y reemplazar el nombre de mi grupo con "📁"
      const subPath = pathParts.slice(myGroupIndex);
      subPath[0] = '📁';
      return subPath.join(' └─ ');
    }
    
    // Si no hay relación, mostrar según el rol
    if ($me?.Role === UserRole.Cashier) {
      return group.Name || '';
    }
    
    // Para Admin, mostrar la jerarquía completa con el separador
    return pathParts.join(' └─ ');
  }

</script>


{#if !playersLoaded}
  {#if loadingPlayers}
    <div class="player-list-state" role="status" aria-live="polite">
      <div class="ui active centered inline loader"></div>
      <p>{$translate('Loading players...')}</p>
    </div>
  {:else}
    <div class="ui negative message player-list-error" role="alert">
      <div class="header">{$translate("Couldn't load players")}</div>
      <button class="ui button" on:click={retryPlayerLoad}>{$translate('Retry')}</button>
    </div>
  {/if}
{:else}
  <h1 class="ui centered dividing header">{$translate('List of players')} ({totalItems})</h1>

  <div class="ui action input">
    <input type="text" bind:value={searchInput} placeholder={$translate('Player name')} autocomplete="off">
    <button class="ui icon button" on:click={clearSearch} aria-label={$translate('Clear search')}>
      <i class="times icon"></i>
    </button>
  </div>
  <select class="ui dropdown location-filter" bind:value={groupFilter} on:change={groupChanged} aria-label={$translate('Location')}>
    <option value={undefined}>{$translate('All locations')}</option>
    {#each groupOptions as option (option.Id)}
      <option value={option.Id}>{option.Path}</option>
    {/each}
  </select>
  {#if loadingPlayers || searchTimer > 0}
    <div class="player-list-updating" role="status" aria-live="polite">
      <i class="spinner loading icon"></i> {$translate('Updating players...')}
    </div>
  {/if}
  {#if playerListError}
    <div class="ui negative message player-list-error" role="alert">
      <span>{$translate("Couldn't load players")}</span>
      <button class="ui button" on:click={retryPlayerLoad}>{$translate('Retry')}</button>
    </div>
  {/if}
{/if}
{#if params?.action == "promo"}
  <Modal dismiss={dismiss}>
    {#if loadingSelectedUser}
      <div class="ui active centered inline loader"></div>
      <p>{$translate('Loading player...')}</p>
    {:else if selectedUserError}
      <div class="ui negative message">{$translate(selectedUserError)}</div>
      <button class="ui button" on:click={dismiss}>{$translate('Close')}</button>
    {:else if !selectedUser}
      <div class="ui negative message" on:click={dismiss}>
        <i class="close icon"></i>
        <div class="header">
          {$translate('No such User')}
        </div>
        <p>Player with id {params?.userid ?? "?"}  {$translate('does not exist')}</p>
      </div>
    {:else}
      <PromotionalcreditsForm player={selectedUser} dismiss={dismiss} type="promo"></PromotionalcreditsForm>
    {/if}
  </Modal>
{/if}
{#if params?.action == "deposit"}
  <Modal dismiss={dismiss}>
    {#if loadingSelectedUser}
      <div class="ui active centered inline loader"></div>
      <p>{$translate('Loading player...')}</p>
    {:else if selectedUserError}
      <div class="ui negative message">{$translate(selectedUserError)}</div>
      <button class="ui button" on:click={dismiss}>{$translate('Close')}</button>
    {:else if !selectedUser}
      <div class="ui negative message" on:click={dismiss}>
        <i class="close icon"></i>
        <div class="header">
          {$translate('No such User')}
        </div>
        <p>Player with id {params?.userid ?? "?"} {$translate('does not exist')}</p>
      </div>
    {:else if !CanDeposit(selectedUser)}
      <div class="ui negative message">
        <i class="close icon" on:click={dismiss}></i>
        <div class="header">
          {$translate('User Disabled')}
        </div>
        <p>{$translate('Cannot make deposits for disabled users')}</p>
        <button class="ui button" on:click={dismiss}>{$translate('Close')}</button>
      </div>
    {:else}
      <WithdrawDepositForm player={selectedUser} dismiss={dismiss} type="deposit"></WithdrawDepositForm>
    {/if}
  </Modal>
{/if}

{#if params?.action == "withdraw"}
  <Modal dismiss={dismiss}>
    {#if loadingSelectedUser}
      <div class="ui active centered inline loader"></div>
      <p>{$translate('Loading player...')}</p>
    {:else if selectedUserError}
      <div class="ui negative message">{$translate(selectedUserError)}</div>
      <button class="ui button" on:click={dismiss}>{$translate('Close')}</button>
    {:else if !selectedUser}
      <div class="ui negative message">
        <div class="header">{$translate('No such User')}</div>
      </div>
    {:else if !CanWithdrawForUser(selectedUser, selectedUser.LastWalletTransaction)}
      <div class="ui negative message">
        <i class="close icon" on:click={dismiss}></i>
        <div class="header">
          {$translate('User Disabled or No Balance')}
        </div>
        <p>{$translate('Cannot make withdrawals for disabled users or users without balance')}</p>
        <button class="ui button" on:click={dismiss}>{$translate('Close')}</button>
      </div>
    {:else}
      <WithdrawDepositForm player={selectedUser} dismiss={dismiss} type="withdraw"></WithdrawDepositForm>
    {/if}
  </Modal>
{/if}

{#if params?.action == "withdrawcashier"}
  <Modal dismiss={dismiss}>
    {#if loadingSelectedUser}
      <div class="ui active centered inline loader"></div>
      <p>{$translate('Loading player...')}</p>
    {:else if selectedUserError}
      <div class="ui negative message">{$translate(selectedUserError)}</div>
      <button class="ui button" on:click={dismiss}>{$translate('Close')}</button>
    {:else if !selectedUser}
      <div class="ui negative message">
        <div class="header">{$translate('No such User')}</div>
      </div>
    {:else if !CanWithdrawForUser(selectedUser, selectedUser.LastWalletTransaction)}
      <div class="ui negative message">
        <i class="close icon" on:click={dismiss}></i>
        <div class="header">
          {$translate('User Disabled or No Balance')}
        </div>
        <p>{$translate('Cannot make withdrawals for disabled users or users without balance')}</p>
        <button class="ui button" on:click={dismiss}>{$translate('Close')}</button>
      </div>
    {:else}
      <WithdrawCashierForm player={selectedUser} dismiss={dismiss}></WithdrawCashierForm>
    {/if}
  </Modal>
{/if}

{#if params?.action == "add"}
  <Modal dismiss={dismiss} size="large">
    <AddPlayer dismiss={dismiss}></AddPlayer>
  </Modal>
{/if}

{#if params?.action == "edit"}
  <Modal dismiss={dismiss} size="large">
    {#if loadingSelectedUser}
      <div class="ui active centered inline loader"></div>
      <p>{$translate('Loading player...')}</p>
    {:else if selectedUserError}
      <div class="ui negative message">{$translate(selectedUserError)}</div>
      <button class="ui button" on:click={dismiss}>{$translate('Close')}</button>
    {:else if !selectedUser}
      <div class="ui negative message">
        <div class="header">{$translate('No such User')}</div>
      </div>
    {:else}
      <EditUserForm user={selectedUser} dismiss={dismiss}></EditUserForm>
    {/if}
  </Modal>
{/if}

{#if playersLoaded}
<a href="#/PlayerList/add" class="ui green button">
  <i class="user plus icon"></i>
 {$translate('Add player')}
</a>
{#if paginatedPlayers.length === 0 && !loadingPlayers && !playerListError && loadedPage === currentPage && loadedSearch === searchInput.trim()}
  <div class="ui message">
    <div class="header">{$translate('No players found')}</div>
  </div>
{:else if paginatedPlayers.length > 0}

<div style="overflow-x: auto;">
<table class="playerlist ui celled unstackable striped selectable very compact unselectable table">
  <thead>
    <tr>
      <th style="width: 0px">#</th>
      <th>{$translate('First Name')}</th>
      <th>{$translate('E-mail')}</th>
      <th>{$translate('Phone Number')}</th>
      <th>{$translate('Birthday')}</th>
      <th>{$translate('Language')}</th>
      <th>{$translate('Registered')}</th>
      <th>{$translate('Group')}</th>
      <th>{$translate('Promo')}</th>
      <th>{$translate('Balance')}</th>
      <th>{$translate('Total')}</th>
      <th style="width: 0px">Allow</th>
      <th style="width: 200px">{$translate('Options')}</th>
    </tr>
  </thead>
  <tbody>
    {#each paginatedPlayers as user}
      <tr>
        <td class="numeric">{user.Id}</td>
        <td class="name-column" title="{user.FirstName} {user.LastName}">
          {user.FirstName} {user.LastName}
        </td>
        <td class="email-column" title={user.Email}>
          {user.Email ?? ""}
        </td>
        <td class="phone-column" title={user.PhoneNumber}>
          {user.PhoneNumber ?? ""}
        </td>
        <td class="phone-column" title={user.BirthdayDay?.toString()}>
         {formatDate(user.BirthdayDay)}
        </td>
        <td class="phone-column" title={user.Language || '-'}>
          {user.Language?.toUpperCase() || '-'}
        </td>
        <td class="registered-column">
          {user.CreatedAt ? formatDateTime(user.CreatedAt) : '-'}
        </td>
        <td class="group-column" title={getGroupPath(user.Group)}>
          <span class="my-group-badge">{getGroupPath(user.Group)}</span>
        </td>
        <td class="numeric balance-column">
          {formatMoney(sumPromotionBalances(user.LastWalletTransaction?.PromotionTransactions || []))}
        </td>
        <td class="numeric balance-column">
         {formatMoney(user.LastWalletTransaction?.MoneyTransaction?.[0]?.Balance|| 0)}
        </td>
        <td class="numeric balance-column">
          {formatMoney(user.LastWalletTransaction?.CashableBalance || 0)}
        </td>
        <td>
          <button class={`ui icon small button ${user.Enabled ? 'positive' : 'negative'}`} on:click={() => DisablePlayer(user)}>
            {#if user.Enabled}
              <i class="check icon"></i>
            {:else}
              <i class="close icon"></i>
            {/if}
          </button>
        </td>
        <td>
          <div style="display: inline-flex; flex-wrap: nowrap; gap: 4px;">
            <button class="ui positive small button" class:disabled={!CanDeposit(user)} on:click={() => push(`#/PlayerList/deposit/${user.Id}${listQuery(loadedPage, true)}`)}>{$translate('Deposit')}</button>
            {#if $me?.Role === 'Cashier'}
              <a class="ui negative small button" class:disabled={!CanWithdrawForUser(user, user.LastWalletTransaction)} href={`#/PlayerList/withdrawcashier/${user.Id}${listQuery(loadedPage, true)}`}>{$translate('Withdraw')}</a>
            {:else}
              <a class="ui negative small button" class:disabled={!CanWithdrawForUser(user, user.LastWalletTransaction)} href={`#/PlayerList/withdraw/${user.Id}${listQuery(loadedPage, true)}`}>{$translate('Withdraw')}</a>
            {/if}
            <a class="ui teal small button" href="#/UserbyTransaction/view/{user.Id}">{$translate('Transactions')}</a>
            <a class="ui primary icon small button" href={`#/playerlist/edit/${user.Id}${listQuery(loadedPage, true)}`}>
              <i class="edit icon"></i>
            </a>
          </div>
        </td>
      </tr>
    {/each}
  </tbody>
</table>
</div>

{#if totalPages > 1}
  <div class="ui pagination menu" style="justify-content: center; margin-top: 1em;">
    <a class="icon item" class:disabled={paginationPage === 1 || loadingPlayers || searchTimer > 0} on:click={previousPage}>
      <i class="left chevron icon"></i>
    </a>
    
    {#if paginationPage > 3}
      <a class="item" on:click={() => goToPage(1)}>1</a>
      {#if paginationPage > 4}
        <div class="disabled item">...</div>
      {/if}
    {/if}
    
    {#each getPageRange() as page}
      <a class="item" class:active={page === paginationPage} class:disabled={loadingPlayers || searchTimer > 0} on:click={() => goToPage(page)}>
        {page}
      </a>
    {/each}
    
    {#if paginationPage < getTotalPages() - 2}
      {#if paginationPage < getTotalPages() - 3}
        <div class="disabled item">...</div>
      {/if}
      <a class="item" on:click={() => goToPage(getTotalPages())}>{getTotalPages()}</a>
    {/if}
    
    <a class="icon item" class:disabled={paginationPage === getTotalPages() || loadingPlayers || searchTimer > 0} on:click={nextPage}>
      <i class="right chevron icon"></i>
    </a>
  </div>
  
  <div class="ui center aligned text" style="margin-top: 0.5em; color: #666;">
    displaying  {((loadedPage - 1) * playersPerPage) + 1} - {Math.min(loadedPage * playersPerPage, totalItems)} of {totalItems} players
  </div>
{/if}
{/if}
{/if}

{#if message.text}
  <div class="ui message {message.type === 'success' ? 'positive' : 'negative'}" transition:fade>
    <i class="close icon" on:click={() => message = { text: '', type: '' }}></i>
    <div class="header">
      {#if message.type === 'success'}
        <i class="check circle icon"></i> Success
      {:else if message.type === 'negative'}
        <i class="ban icon"></i> Player Disabled
      {:else}
        <i class="exclamation triangle icon"></i> Error
      {/if}
    </div>
    <p>{message.text}</p>
  </div>
{/if}

<style>
  .center{
    text-align: center;
  }

  .ui.input {
    margin-bottom: 1em;
  }

  .player-list-state {
    min-height: 35vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
  }

  .player-list-updating {
    margin: 0.5em 0 1em;
    color: #666;
  }

  .player-list-error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75em;
  }

  .player-list-error .button {
    flex: 0 0 auto;
  }

  .playerlist {
    white-space: nowrap;
    font-size: 0.85em !important;
  }

  .playerlist td {
    padding: 0.4em !important;
    text-align: center;
  }

  .playerlist th {
    padding: 0.3em 0.4em !important;
    text-align: center;
  }

  .numeric {
    font-family: monospace;
    text-align: right !important;
  }

  .ui.small.button {
    padding: 0.6em 0.8em !important;
    font-size: 0.85em !important;
    margin: 0 !important;
  }

  .ui.table td {
    padding: 0.3em !important;
  }

  /* Ajustar anchos de columnas específicas */
  .id-column {
    width: 40px !important;
  }

  .name-column {
    width: 100px !important;
  }

  .email-column {
    width: 150px !important;
  }

  .phone-column {
    width: 80px !important;
  }

  .balance-column {
    width: 70px !important;
  }

  .registered-column {
    white-space: nowrap;
  }

  .location-filter {
    margin-left: 0.5em;
    max-width: 320px;
  }

  .group-column {
    width: 180px !important;
    text-align: left !important;
    max-width: 180px !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .my-group-badge {
    background-color: #e3f2fd;
    color: #1976d2;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.8em;
    font-weight: bold;
    border: 1px solid #2196f3;
  }

  .child-group-badge {
    background-color: #f3e5f5;
    color: #7b1fa2;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.8em;
    border: 1px solid #9c27b0;
  }

  .parent-group-badge {
    background-color: #fff3e0;
    color: #f57c00;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.8em;
    border: 1px solid #ff9800;
  }

  .hierarchy-group-badge {
    background-color: #e8f5e8;
    color: #2e7d32;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.8em;
    font-weight: bold;
    border: 1px solid #4caf50;
  }

  .other-group-badge {
    background-color: #f5f5f5;
    color: #666;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.8em;
    border: 1px solid #ddd;
  }

  .options-column {
    width: 220px !important;
    white-space: nowrap;
    padding: 0.3em !important;
  }

  .ui.message {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
    margin: 0;
    padding: 1.5em;
    min-width: 300px;
    max-width: 500px;
    text-align: center;
    box-shadow: 0 3px 6px rgba(0,0,0,0.16);
    border-radius: 8px;
  }

  .ui.message .close.icon {
    position: absolute;
    top: 0.8em;
    right: 0.8em;
    cursor: pointer;
    opacity: 0.7;
  }

  .ui.message .close.icon:hover {
    opacity: 1;
  }

  .ui.message .header {
    margin-bottom: 0.5em;
    font-weight: bold;
  }

  /* Estilos para la paginación */
  .ui.pagination.menu {
    border-radius: 0.28571429rem;
    box-shadow: 0 1px 2px 0 rgba(34, 36, 38, 0.15);
  }

  .ui.pagination.menu .item {
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .ui.pagination.menu .item:hover:not(.disabled) {
    background-color: #f9fafb;
  }

  .ui.pagination.menu .item.active {
    background-color: #2185d0;
    color: white;
    font-weight: bold;
  }

  .ui.pagination.menu .item.disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .ui.pagination.menu .item.disabled:hover {
    background-color: transparent;
  }
  @media (max-width: 768px) {
    .playerlist th,
    .playerlist td {
      font-size: 12px;
      padding: 6px;
    }

    .playerlist .ui.button {
      font-size: 10px;
      padding: 4px 6px;
    }

    .playerlist .name-column,
    .playerlist .email-column,
    .playerlist .phone-column,
    .playerlist .group-column {
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .playerlist .ui.small.button {
      font-size: 9px !important;
      padding: 3px 4px !important;
    }
  }
</style>
